// React context for cookie consent. Wraps the app, exposes the current choice
// plus accept/reject/open actions, and triggers analytics init on acceptance.

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  type ConsentChoice,
  getConsent,
  subscribeConsent,
  setConsent as persistConsent,
} from '@/analytics/consent';
import { marketingAnalytics } from '@/analytics/lazyAnalytics';

interface CookieConsentContextValue {
  /** Stored choice, or `null` when the visitor has not decided yet. */
  choice: ConsentChoice | null;
  /** True when the banner has been re-opened via "Cookie settings". */
  isOpen: boolean;
  /** True only after consented analytics initialization has completed. */
  analyticsReady: boolean;
  /** Re-open the banner so a prior choice can be changed. */
  open: () => void;
  accept: () => void;
  reject: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  // Lazy initializer reads localStorage on the client's first render (and null
  // during SSR), so returning visitors never see a banner flash.
  const [choice, setChoice] = useState<ConsentChoice | null>(() => getConsent());
  const [isOpen, setIsOpen] = useState(false);
  const [analyticsReady, setAnalyticsReady] = useState(false);

  // Reconcile explicit decisions, other tabs, expiry and returning browser focus.
  useEffect(() => {
    let active = true;
    let revision = 0;
    const synchronize = () => {
      const current = ++revision;
      const stored = getConsent();
      setChoice(stored);
      setAnalyticsReady(false);
      if (stored !== 'accepted') {
        marketingAnalytics.teardown();
        return;
      }
      void marketingAnalytics.init().then(ready => {
        if (active && revision === current) setAnalyticsReady(ready && getConsent() === 'accepted');
      });
    };
    const unsubscribe = subscribeConsent(synchronize);
    window.addEventListener('focus', synchronize);
    synchronize();
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener('focus', synchronize);
    };
  }, []);

  const accept = () => {
    persistConsent('accepted');
    setIsOpen(false);
  };
  const reject = () => {
    persistConsent('rejected');
    // Subscribers stop immediately, including when storage refuses withdrawal.
    setIsOpen(false);
  };

  const open = () => setIsOpen(true);

  return (
    <CookieConsentContext.Provider
      value={{ choice, isOpen, analyticsReady, open, accept, reject }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextValue => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return ctx;
};
